import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../API/webserviceUrl";

interface GroupMember {
  id: string;
  name: string;
}

interface Group {
  id: string;
  name: string;
  members: GroupMember[];
  leadId: string | null;
  score: number;
  evaluationsReceived: number;
}

// ── API response shapes ─────────────────────────────────────
interface EvalApiMember {
  student_id: string;
  name: string;
  is_lead: boolean;
}

interface EvalApiGroup {
  group_number: number;
  group_id: number;
  member_count: number;
  members: EvalApiMember[];
  score: string | number;
  evaluations_received: number;
}

interface EvalApiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: {
    total_groups: number;
    groups: EvalApiGroup[];
  };
}

function mapApiGroup(g: EvalApiGroup): Group {
  const leadMember = g.members.find((m) => m.is_lead);
  return {
    id: String(g.group_id),
    name: `Group ${g.group_id}`,
    members: g.members.map((m) => ({ id: m.student_id, name: m.name })),
    leadId: leadMember ? leadMember.student_id : null,
    score: Number(g.score) || 0,
    evaluationsReceived: g.evaluations_received ?? 0,
  };
}

export default function TopGroupEvaluation() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = localStorage.getItem("token");
  async function fetchEvaluatedGroups() {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<EvalApiResponse>(
        `${BASE_URL}/get-all-evaluated-groups`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.data?.success) {
        const apiGroups = res.data.data?.groups ?? [];
        setGroups(apiGroups.map(mapApiGroup));
      } else {
        setError(res.data?.message || "Couldn't load group evaluations.");
      }
    } catch (err) {
      console.error("Failed to fetch evaluated groups", err);
      setError("Couldn't load group evaluations.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEvaluatedGroups();
  }, []);

  function initials(name: string) {
    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("");
  }

  return (
    <div className="page-wrapper">
      <div className="content mb-3">
        <style>{`* {
                  box-sizing: border-box;
                }

                .top-root {
                  container-type: inline-size;
                  container-name: top;
                  width: 100%;
                  min-width: 0;
                }

                .top-page {
                  margin: 0;
                  font-family: "Inter", "Segoe UI", Arial, sans-serif;
                  color: #1a1a1a;
                  min-width: 0;
                }

                .top-title {
                  margin: 0 0 2px;
                  font-size: 20px;
                  font-weight: 600;
                  letter-spacing: -0.01em;
                }

                .top-subtitle {
                  margin: 0 0 16px;
                  font-size: 13px;
                  color: #6b7280;
                  line-height: 1.45;
                }

                .top-groups-heading {
                  font-size: 14.5px;
                  font-weight: 600;
                  margin: 0 0 8px;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                }

                .top-groups-count-pill {
                  font-size: 11px;
                  font-weight: 700;
                  color: #6b7280;
                  background: #f3f4f6;
                  border-radius: 999px;
                  padding: 2px 8px;
                }

                .top-groups {
                  display: grid;
                  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                  gap: 10px;
                }

                .top-group-card {
                  background: #fff;
                  border: 1px solid #ececec;
                  border-radius: 14px;
                  padding: 14px;
                  min-width: 0;
                  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.03),
                    0 6px 16px rgba(16, 24, 40, 0.035);
                  transition: box-shadow 0.18s, transform 0.18s, border-color 0.18s;
                  display: flex;
                  flex-direction: column;
                }

                .top-group-card:hover {
                  box-shadow: 0 2px 4px rgba(16, 24, 40, 0.05),
                    0 14px 28px rgba(16, 24, 40, 0.08);
                  transform: translateY(-2px);
                  border-color: #e2e2e2;
                }

                .top-group-head {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  gap: 8px;
                  margin-bottom: 3px;
                  flex-shrink: 0;
                }

                .top-group-head h3 {
                  margin: 0;
                  font-size: 14.5px;
                  font-weight: 800;
                  overflow-wrap: anywhere;
                  min-width: 0;
                }

                .top-score-badge {
                  flex-shrink: 0;
                  font-size: 13px;
                  font-weight: 800;
                  padding: 4px 12px;
                  border-radius: 999px;
                  white-space: nowrap;
                  background: #eefaf1;
                  color: #16a34a;
                }

                .top-score-badge.no-score {
                  background: #f3f4f6;
                  color: #9ca3af;
                }

                .top-eval-count {
                  font-size: 11px;
                  color: #9ca3af;
                  font-weight: 600;
                  flex-shrink: 0;
                }

                .top-group-count {
                  font-size: 11px;
                  color: #9ca3af;
                  margin: 2px 0 10px;
                  font-weight: 600;
                  flex-shrink: 0;
                }

                .top-member-list {
                  display: flex;
                  flex-direction: column;
                  gap: 6px;
                }

                .top-member-row {
                  display: flex;
                  align-items: center;
                  gap: 9px;
                  border: 1.5px solid #f0f0ee;
                  border-radius: 10px;
                  padding: 7px 9px;
                }

                .top-chip-avatar {
                  flex-shrink: 0;
                  width: 25px;
                  height: 25px;
                  border-radius: 50%;
                  background: #e7f5ee;
                  color: #16a34a;
                  font-size: 10px;
                  font-weight: 800;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }

                .top-member-name {
                  font-size: 12.5px;
                  font-weight: 600;
                  flex: 1 1 auto;
                  min-width: 0;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                }

                .top-lead-tag {
                  flex-shrink: 0;
                  font-size: 9.5px;
                  font-weight: 800;
                  color: #16a34a;
                  background: #d9f2e3;
                  padding: 3px 8px;
                  border-radius: 999px;
                  letter-spacing: 0.02em;
                  text-transform: uppercase;
                }

                .top-empty-state {
                  font-size: 13px;
                  color: #9ca3af;
                  text-align: center;
                  padding: 20px 0;
                  border: 1.5px dashed #e6e3d9;
                  border-radius: 14px;
                  background: #fbfbfa;
                }

                .top-inline-link-btn {
                  border: none;
                  background: none;
                  color: #16a34a;
                  font-weight: 700;
                  cursor: pointer;
                  text-decoration: underline;
                  padding: 0;
                  font-size: 12px;
                }

                @container top (max-width: 640px) {
                  .top-groups {
                    grid-template-columns: 1fr;
                  }
                }

                @container top (max-width: 420px) {
                  .top-page {
                    padding: 10px 10px 18px;
                  }
                }

                @supports not (container-type: inline-size) {
                  @media (max-width: 640px) {
                    .top-groups {
                      grid-template-columns: 1fr;
                    }
                  }
                  .top-group-card {
                  display: flex;
                  flex-direction: column;
                  height: 100%; /* or set a fixed height if parent requires */
                }
                  .top-member-list {
                  flex: 1;              /* ✅ takes remaining space */
                  overflow-y: auto;     /* optional if many members */
                }
                }`}
        </style>

        <div className="top-root">
          <div className="top-page">
            <h1 className="top-title" style={{ marginBottom: "10px" }}>
              Group Evaluation
            </h1>

            {loading && groups.length === 0 && (
              <p className="top-empty-state">Loading group evaluations…</p>
            )}

            {!loading && error && (
              <p className="top-empty-state">
                {error}{" "}
                <button
                  type="button"
                  className="top-inline-link-btn"
                  onClick={fetchEvaluatedGroups}
                >
                  Retry
                </button>
              </p>
            )}

            {!loading && !error && groups.length === 0 && (
              <p className="top-empty-state">No groups found.</p>
            )}

            {!loading && !error && groups.length > 0 && (
              <>
                <h2 className="top-groups-heading mt-3 mb-3">
                  Groups
                  <span className="top-groups-count-pill">{groups.length}</span>
                </h2>
                <div className="top-groups">
                  {groups.map((g) => (
                    <div key={g.id} className="top-group-card">
                      <div className="top-group-head">
                        <h3 style={{ fontWeight: "600" }}>{g.name}</h3>
                      </div>
                      <div className="top-group-count">
                        {g.members.length} member
                        {g.members.length !== 1 ? "s" : ""}
                      </div>

                      <div className="top-member-list">
                        {g.members.map((m) => (
                          <div key={m.id} className="top-member-row">
                            <span className="top-chip-avatar">
                              {initials(m.name)}
                            </span>
                            <span className="top-member-name">{m.name}</span>
                            {g.leadId === m.id && (
                              <span className="top-lead-tag">Lead</span>
                            )}
                          </div>
                        ))}
                      </div>
                      <div
                        className="top-group-head"
                        style={{
                          marginTop: "auto", // ✅ pushes it to bottom
                          justifyContent: "space-between",
                          display: "flex",
                          alignItems: "flex-end",
                          marginBottom: "10px",
                          paddingTop: "20px",
                        }}
                      >
                        <span className="top-eval-count">
                          {g.evaluationsReceived} evaluation
                          {g.evaluationsReceived !== 1 ? "s" : ""} received
                        </span>
                        <span
                          className={`top-score-badge ${g.evaluationsReceived === 0 ? "no-score" : ""
                            }`}
                        >
                          {g.evaluationsReceived === 0
                            ? "Not yet scored"
                            : `Score ${g.score}`}
                        </span>
                      </div>
                      {g.evaluationsReceived === 0 ? (
                        <span
                          className="top-inline-link-btn disabled"
                          style={{
                            marginTop: "10px",
                            textAlign: "center",
                            opacity: 0.5,
                            cursor: "not-allowed",
                            pointerEvents: "none",
                            textDecoration: "none"
                          }}
                        >
                          View
                        </span>
                      ) : (
                        <Link
                          to={`/view-assessments/${g.id}`}
                          className="top-inline-link-btn"
                          style={{
                            marginTop: "10px",
                            textAlign: "center",
                            textDecoration: "none"
                          }}
                        >
                          View
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
